// src/pages/Dashboard.js
import React, { useEffect, useMemo, useState } from "react";
import "./Dashboard.css";
import api from "../api";
import { useSklad } from "../SkladContext";

/* ============ Decimal helpers ============ */
// "12,30" -> "12.30"
const normDecStr = (v) => {
  if (v == null) return "0";
  let s = String(v).trim().replace(",", ".");
  s = s.replace(/[^\d.-]/g, "");
  if (s.includes("-")) s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");
  const i = s.indexOf(".");
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  if (s === "" || s === "-" || s === "." || s === "-.") return "0";
  return s;
};
const toNum = (v) => {
  const n = Number(normDecStr(v));
  return Number.isFinite(n) ? n : 0;
};
const money = (v, { minFrac = 2, maxFrac = 2, suffix = " so'm" } = {}) =>
  toNum(v).toLocaleString("uz-UZ", {
    minimumFractionDigits: minFrac,
    maximumFractionDigits: maxFrac,
  }) + suffix;

/* Haftalik nomlar (tashqarida – dependency muammosiz) */
const WEEKDAY_LABELS = [
  "Yakshanba",
  "Dushanba",
  "Seshanba",
  "Chorshanba",
  "Payshanba",
  "Juma",
  "Shanba",
];

const normYMD = (s) => String(s || "").slice(0, 10);

/* =======================================
   ASOSIY DASHBOARD KOMPONENT
   ======================================= */
export default function AnalyticsDashboard() {
  const { activeSklad } = useSklad();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState({
    date_from: "",
    date_to: "",
    top_products: [],
    top_customers: [],
    top_profit_products: [],
    daily: [],
    pay_split: { cash_pct: 0, credit_pct: 0, cash_total: 0, credit_total: 0 },
  });

  /* ------- Backenddan ma'lumot olish ------- */
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const params = {};
        if (from) params.date_from = from;
        if (to) params.date_to = to;
        if (activeSklad && activeSklad !== "all") params.sklad = activeSklad;

        const res = await api.get("analytics/dashboard/", { params });
        if (cancelled) return;

        const data = res.data || {};

        // Birinchi marta backenddan kelgan sana oraliğini inputlarga yozib qo’yamiz
        if (!from && data.date_from) setFrom(normYMD(data.date_from));
        if (!to && data.date_to) setTo(normYMD(data.date_to));

        setAnalytics({
          date_from: normYMD(data.date_from),
          date_to: normYMD(data.date_to),
          top_products: data.top_products || [],
          top_customers: data.top_customers || [],
          top_profit_products: data.top_profit_products || [],
          daily: data.daily || [],
          pay_split:
            data.pay_split || {
              cash_pct: 0,
              credit_pct: 0,
              cash_total: 0,
              credit_total: 0,
            },
        });
      } catch (e) {
        console.log("Dashboard load error", e?.response?.status, e?.response?.data);
        if (!cancelled) {
          setError("Ma’lumotlarni yuklashda xatolik yuz berdi.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [from, to, activeSklad]);

  /* ------- Derived ma'lumotlar ------- */
  const top10Products = analytics.top_products || [];
  const topCustomers = analytics.top_customers || [];
  const topProfitProducts = analytics.top_profit_products || [];
  const paySplit = analytics.pay_split || {
    cash_pct: 0,
    credit_pct: 0,
    cash_total: 0,
    credit_total: 0,
  };

  // Haftalik: daily[] ni haftaning kunlariga yig'amiz
  const weekly = useMemo(() => {
    const vals = new Array(7).fill(0);
    (analytics.daily || []).forEach((row) => {
      const d = new Date(normYMD(row.date) + "T00:00:00");
      if (!Number.isNaN(d.getTime())) {
        const idx = d.getDay(); // 0-6
        vals[idx] += toNum(row.total);
      }
    });
    return { labels: WEEKDAY_LABELS, values: vals };
  }, [analytics]);

  // Oylik: daily[] ni sanaga qarab tartiblab, ketma-ket 30 tagacha qo'yamiz
  const monthly = useMemo(() => {
    const daily = analytics.daily || [];
    const sorted = [...daily].sort((a, b) =>
      normYMD(a.date).localeCompare(normYMD(b.date))
    );
    const sliced = sorted.slice(0, 30);
    return {
      labels: sliced.map((r) => normYMD(r.date).slice(8, 10)),
      values: sliced.map((r) => toNum(r.total)),
    };
  }, [analytics]);

  const cashPct = Number.isFinite(paySplit.cash_pct) ? paySplit.cash_pct : 0;
  const creditPct = Number.isFinite(paySplit.credit_pct)
    ? paySplit.credit_pct
    : 0;

  return (
    <div className="ad-wrap">
      {/* Filtrlar */}
      <div className="ad-filters fr-panel">
        <div className="f-title">Dashboard</div>
        <div className="f-rows">
          <label className="f-item">
            <span>Boshlanish sanasi</span>
            <input
              type="date"
              className="input"
              value={from}
              onChange={(e) => setFrom(normYMD(e.target.value))}
            />
          </label>
          <label className="f-item">
            <span>Tugash sanasi</span>
            <input
              type="date"
              className="input"
              value={to}
              onChange={(e) => setTo(normYMD(e.target.value))}
            />
          </label>
          {loading && <div className="f-loading">Yuklanmoqda…</div>}
          {error && !loading && <div className="f-error">{error}</div>}
        </div>
      </div>

      <div className="ad-grid">
        {/* Top 10 mahsulotlar */}
        <section className="card span-12 fr-card">
          <h3 className="card-title">Eng ko‘p sotilgan mahsulotlar (Top 10)</h3>
          <div className="top-products">
            {top10Products.length > 0 ? (
              top10Products.map((p) => (
                <div key={p.product_id} className="tp-item">
                  <div className="tp-name">{p.name}</div>
                  <div className="tp-sub">{p.sku}</div>
                  <div className="tp-row">
                    <span className="pill">
                      Sotildi: <b>{toNum(p.qty).toLocaleString("uz-UZ")}</b> dona
                    </span>
                    <span className="pill">
                      Jami: <b>{money(p.revenue)}</b>
                    </span>
                    <span className="pill green">
                      Foyda: <b>{money(p.profit)}</b>
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty">Ma’lumot yo‘q</div>
            )}
          </div>
        </section>

        {/* Top mijozlar */}
        <section className="card span-12 fr-card">
          <h3 className="card-title">Eng ko‘p savdo qilgan mijozlar</h3>
          <div className="customers">
            {topCustomers.length > 0 ? (
              topCustomers.map((c) => (
                <div key={c.id} className="cust-card">
                  <div className="cust-name">{c.name}</div>
                  <div className="cust-phone">{c.phone || "—"}</div>
                  <div className="cust-metrics">
                    <span className="pill green">
                      Savdo: <b>{money(c.revenue)}</b>
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty">Ma’lumot yo‘q</div>
            )}
          </div>
        </section>

        {/* Haftalik grafik */}
        <section className="card span-6 fr-card">
          <h3 className="card-title">Haftalik grafik tahlil</h3>
          <Bars labels={weekly.labels} values={weekly.values} color="#d5362a" />
        </section>

        {/* Oylik grafik */}
        <section className="card span-6 fr-card">
          <h3 className="card-title">Oylik grafik tahlil</h3>
          <Line labels={monthly.labels} values={monthly.values} color="#d5362a" />
        </section>

        {/* Eng ko‘p foyda keltirgan mahsulotlar */}
        <section className="card span-8 fr-card">
          <h3 className="card-title">Eng ko‘p foyda keltirgan mahsulotlar</h3>
          <div className="profit-list">
            {topProfitProducts.length > 0 ? (
              topProfitProducts.map((p) => (
                <div key={p.product_id} className="profit-row">
                  <div className="grow">
                    <div className="name">{p.name}</div>
                    <div className="sub">{p.sku}</div>
                  </div>
                  <div className="mono pill">
                    Sotildi:{" "}
                    <b>{toNum(p.qty).toLocaleString("uz-UZ")}</b> dona
                  </div>
                  <div className="mono pill green">
                    Foyda: <b>{money(p.profit)}</b>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty">Ma’lumot yo‘q</div>
            )}
          </div>
        </section>

        {/* Naxt / Nasiya taqsimoti */}
        <section className="card span-4 fr-card">
          <h3 className="card-title">Naxt / Nasiya taqsimoti</h3>
          <div className="split-wrap">
            <div className="split-legend">
              <div className="leg">
                <span className="dot green" /> naxt <b>{cashPct}%</b>
              </div>
              <div className="leg">
                <span className="dot red" /> nasiya <b>{creditPct}%</b>
              </div>
            </div>
            <DonutPct cashPct={cashPct} creditPct={creditPct} />
          </div>
        </section>
      </div>
    </div>
  );
}

/* ================== Mini komponentlar ================== */

function DonutPct({ cashPct, creditPct }) {
  const safeCash = Math.max(0, Math.min(100, Number(cashPct) || 0));
  const deg = (safeCash / 100) * 360;
  const bg = `conic-gradient(#3bbf7a ${deg}deg, #bf3b3b ${deg}deg)`;
  return (
    <div className="donut-pct" style={{ background: bg }}>
      <div className="donut-hole" />
    </div>
  );
}

function Bars({ labels, values, color = "#d5362a" }) {
  const max = Math.max(...values.map(toNum), 1);
  return (
    <div className="bars">
      {labels.map((lb, i) => {
        const perc = (toNum(values[i]) / max) * 100;
        const h = Math.max(4, perc); // kamida 4%
        return (
          <div key={lb} className="bar-col">
            <div className="bar-box">
              <div className="bar" style={{ height: `${h}%`, background: color }} />
            </div>
            <div className="bar-label">{lb}</div>
          </div>
        );
      })}
    </div>
  );
}

function Line({ labels, values, color = "#d5362a" }) {
  const N = values.length || 1;
  const w = 720;
  const h = 240;
  const pad = 20;
  const max = Math.max(...values.map(toNum), 1);
  const stepX = (w - pad * 2) / (N - 1 || 1);

  const points = values.map((v, i) => {
    const x = pad + i * stepX;
    const y =
      h - pad - (toNum(v) / max || 0) * (h - pad * 2);
    return [x, y];
  });

  const d =
    points.length > 0
      ? "M " + points.map((p) => p.join(",")).join(" L ")
      : "";

  return (
    <div className="line-wrap">
      <svg className="line-svg" viewBox={`0 0 ${w} ${h}`}>
        {/* Vertical grid lines */}
        <g stroke="#e8e8ee">
          {new Array(12).fill(0).map((_, i) => {
            const x = pad + ((w - pad * 2) / 12) * i;
            return <line key={i} x1={x} y1={pad} x2={x} y2={h - pad} />;
          })}
        </g>
        {d && <path d={d} fill="none" stroke={color} strokeWidth="2" />}
      </svg>
      <div className="days-row">
        {labels.map((m, idx) => (
          <span key={idx}>{m}</span>
        ))}
      </div>
    </div>
  );
}
